import { ref, computed } from 'vue';
import { logger } from '@/common/services/config';

// Global mockup state
const isMockupMode = ref(false);

// Available mockup features
export interface MockupFeature {
  key: string;
  name: string;
  description: string;
  enabled: boolean;
}

const mockupFeatures = ref<MockupFeature[]>([
  {
    key: 'absences',
    name: 'Absences',
    description: 'Utiliser les données de test pour les absences',
    enabled: true,
  },
  {
    key: 'agenda',
    name: 'Agenda',
    description: "Utiliser les données de test pour l'agenda",
    enabled: true,
  },
  {
    key: 'news',
    name: 'Actualités',
    description: 'Utiliser les données de test pour les actualités',
    enabled: true,
  },
  {
    key: 'wardMenu',
    name: 'gestion du menu',
    description: 'Utiliser les données de test pour la gestion du menu',
    enabled: true,
  },
  {
    key: 'notes',
    name: 'Notes',
    description: 'Utiliser les données de test pour les notes',
    enabled: true,
  },
  {
    key: 'studyAbroad',
    name: 'Study Abroad',
    description: "Utiliser les données de test pour la gestion des campagnes d'expatriation",
    enabled: true,
  },
  {
    key: 'search',
    name: 'Search',
    description: "Utiliser les données de test pour la recherche d'utilisateurs",
    enabled: true,
  },
]);

export class GlobalMockupService {
  /**
   * Get the current mockup mode state
   */
  static get isMockupMode() {
    return isMockupMode.value;
  }

  /**
   * Toggle global mockup mode
   */
  static toggleMockupMode() {
    isMockupMode.value = !isMockupMode.value;

    // Store in localStorage for persistence
    localStorage.setItem('emnverse_mockup_mode', isMockupMode.value.toString());

    // Reload the page to apply changes
    window.location.reload();
  }

  /**
   * Enable mockup mode
   */
  static enableMockupMode() {
    isMockupMode.value = true;
    localStorage.setItem('emnverse_mockup_mode', 'true');
    window.location.reload();
  }

  /**
   * Disable mockup mode
   */
  static disableMockupMode() {
    isMockupMode.value = false;
    localStorage.setItem('emnverse_mockup_mode', 'false');
    window.location.reload();
  }

  /**
   * Initialize mockup mode from localStorage
   */
  static initializeMockupMode() {
    const stored = localStorage.getItem('emnverse_mockup_mode');
    isMockupMode.value = stored === 'true';
  }

  /**
   * Check if a specific feature mockup is enabled
   */
  static isFeatureMockupEnabled(featureKey: string): boolean {
    if (!isMockupMode.value) return false;

    const feature = mockupFeatures.value.find((f) => f.key === featureKey);
    return feature?.enabled ?? false;
  }

  /**
   * Get all available mockup features
   */
  static getMockupFeatures(): MockupFeature[] {
    return mockupFeatures.value;
  }

  /**
   * Toggle a specific feature mockup
   */
  static toggleFeatureMockup(featureKey: string) {
    const feature = mockupFeatures.value.find((f) => f.key === featureKey);
    if (feature) {
      feature.enabled = !feature.enabled;

      // Store in localStorage
      localStorage.setItem('emnverse_mockup_features', JSON.stringify(mockupFeatures.value));
    }
  }

  /**
   * Initialize feature settings from localStorage
   */
  static initializeFeatureSettings() {
    const stored = localStorage.getItem('emnverse_mockup_features');
    if (stored) {
      try {
        const features = JSON.parse(stored);
        features.forEach((storedFeature: MockupFeature) => {
          const feature = mockupFeatures.value.find((f) => f.key === storedFeature.key);
          if (feature) {
            feature.enabled = storedFeature.enabled;
          }
        });
      } catch (error) {
        logger.warn('Failed to load mockup feature settings:', error);
      }
    }
  }
}

// Vue composable for reactive mockup state
export function useMockupMode() {
  return {
    isMockupMode: computed(() => isMockupMode.value),
    toggleMockupMode: GlobalMockupService.toggleMockupMode,
    enableMockupMode: GlobalMockupService.enableMockupMode,
    disableMockupMode: GlobalMockupService.disableMockupMode,
    isFeatureMockupEnabled: GlobalMockupService.isFeatureMockupEnabled,
    getMockupFeatures: GlobalMockupService.getMockupFeatures,
    toggleFeatureMockup: GlobalMockupService.toggleFeatureMockup,
  };
}

// Initialize on module load
GlobalMockupService.initializeMockupMode();
GlobalMockupService.initializeFeatureSettings();
